import { Component, Input, Inject, Optional } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { DirectivesModule, MaterialModule } from '@huge-monorepo/shared/modules';
import { FormControl, Validators } from '@angular/forms';
import {
  DialogService,
  SharedGenericSingletonService,
  SharedTeltonikaSingletonService,
} from '@huge-monorepo/shared/services';
import { ToastrService } from 'ngx-toastr';
import { IHIPTeltonikaService, ICustomerTeltonikaService, ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TeltonikaUpdateDevicePollingModalComponent } from '../teltonika-update-device-polling-modal/teltonika-update-device-polling-modal.component';
import { TeltonikaTaskState } from '../teltonika-task-polling-modal/teltonika-task-polling-modal.component';
import { ToolsModule } from '@huge-monorepo/shared/tools';

@Component({
  selector: 'huge-monorepo-teltonika-update-device-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule, ToolsModule],
  templateUrl: './teltonika-update-device-modal.component.html',
  styleUrl: './teltonika-update-device-modal.component.scss',
})
export class TeltonikaUpdateDeviceModalComponent {
  teltonikaService = this.sharedTeltonikaService.getService();
  ip: FormControl<string | null> = new FormControl(null, Validators.required);
  username: FormControl<string | null> = new FormControl(null, Validators.required);
  password: FormControl<string | null> = new FormControl(null, Validators.required);
  ftpPort: FormControl<number | null> = new FormControl(null, Validators.required);
  ftpUsername: FormControl<string | null> = new FormControl(null, Validators.required);
  ftpPassword: FormControl<string | null> = new FormControl(null, Validators.required);
  uploadCSV: boolean = false;
  files: FileList | null;
  working: boolean = false;
  isFileUploaded: boolean = false;
  isSingleDevice: boolean = false;
  updating: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() private dialogRef: MatDialogRef<TeltonikaUpdateDeviceModalComponent>,
    private sharedGenericService: SharedGenericSingletonService<ICustomerGenericService | IHIPGenericService>,
    private toastr: ToastrService,
    private location: Location,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
  ) { }

  ngOnInit() {
    this.updating = false;
    if (this.data && this.data['SingleDevice']) {
      this.isSingleDevice = true;
      this.ip.setValue(this.data['IP']);
      this.username.setValue(this.data['Username']);
      this.ftpPort.setValue(this.data['FTPPort']);
      this.ftpUsername.setValue(this.data['FTPUsername']);
    } else {
      this.username.setValue("admin");
      this.ftpPort.setValue(22);
      this.ftpUsername.setValue("root");
    }
  }

  updateDevice(): void {
    // Handle the form submission or file upload here
    let updateObj: any;

    if (!this.uploadCSV) {
      if (
        this.ip.invalid ||
        this.username.invalid ||
        this.password.invalid ||
        this.ftpPort.invalid ||
        this.ftpUsername.invalid ||
        this.ftpPassword.invalid
      ) {
        this.ip.markAllAsTouched();
        this.username.markAllAsTouched();
        this.password.markAllAsTouched();
        this.ftpPort.markAllAsTouched();
        this.ftpUsername.markAllAsTouched();
        this.ftpPassword.markAllAsTouched();
        return;
      }

      updateObj = {
        IP: this.ip.value,
        Username: this.username.value,
        Password: this.password.value,
        FTPPort: this.ftpPort.value,
        FTPUsername: this.ftpUsername.value,
        FTPPassword: this.ftpPassword.value,
      };
    } else {
      if (this.files == null) {
        this.sharedGenericService.service.createToast('Error', 'Please upload a file', 'error');
        return;
      }
      this.updating = true;

      updateObj = { files: this.files };
    }

    this.teltonikaService.updateDevice(updateObj).subscribe({
      next: (resp) => {
        this.updating = false;
        if (!resp) {
          this.toastr.error('Error Updating Devices');
          this.dialogRef.close(null);
        } else {
          this.toastr.success('Updating Devices');

          const dialogPollingRef = this.dialog.open(TeltonikaUpdateDevicePollingModalComponent, {
            width: '60vw',
            data: {
              sessionGuid: resp['sessionGuid'],
              devices: resp['IPs'].map(device => ({
                IP: device,
                Status: TeltonikaTaskState.loading
              }))
            }
          });
          this.dialogService.RegisterDialog(dialogPollingRef);

          dialogPollingRef.afterClosed().subscribe(() => {
            this.dialogService.DeRegisterDialog(dialogPollingRef);
          });

          if (this.dialogRef) {
            this.dialogRef.close('success');
          } else {
            this.location.back();
          }
        }
      },
      error: (err) => {
        this.toastr.error('Error Updating Devices');
        this.dialogRef.close(null);
      },
    });
  }

  cancel(): void {
    if (this.dialogRef && typeof this.dialogRef.close === 'function') {
      this.dialogRef.close();
    } else {
      this.location.back();
    }
  }

  handleDroppedFiles(files) {
    let input = document.getElementById('fileInput') as HTMLInputElement;
    if (input != null) {
      this.onFileInput(files);
    }
  }

  onFileInput(files: FileList) {
    if (this.working) {
      this.sharedGenericService.service.createToast('Busy Uploading', 'You cannot change the file list whilst uploading. Please wait for the current process to finish', 'error');
      return;
    }

    if (files.length === 1) {
      if (files[0].name && !files[0].name.endsWith('.csv')) {
        this.sharedGenericService.service.createToast('Error', 'File must be CSV', 'error');
        return;
      }
      this.files = files;
      this.isFileUploaded = true;
      return;
    } else {
      this.sharedGenericService.service.createToast('Error', 'Please select only one file', 'error');
      return;
    }
  }

  deleteFile() {
    this.files = null;
    this.isFileUploaded = false;
  }

  downloadTemplate() {
    this.teltonikaService.downloadUpdateTeltonikaDeviceTemplate().subscribe({});
  }
}
